import { useCallback, useEffect, useState } from "react";
import { Group, Text, Progress } from "@mantine/core";
import { IconUpload, IconPhoto, IconX } from "@tabler/icons-react";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { uploadImage, ImageAlreadyExistsError } from "../api";
import { rem } from "@mantine/core";
import mixpanel from "../tracking";
import { notifications } from "@mantine/notifications";

function ImageUploader({ inline, uploadCompleted }) {
  const [uploadQueue, setUploadQueue] = useState([]);
  const [activeUploads, setActiveUploads] = useState(0);
  const [totalUploaded, setTotalUploaded] = useState(0);

  let minHeight = inline ? 0 : { base: 160, sm: 220 };
  let iconSize = inline ? 40 : 52;
  let textSize = inline ? "sm" : "xl";

  const MAX_PARALLEL_UPLOADS = 3;

  const handleUpload = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        // console.debug("HandleUpload for file", file);
        uploadImage(file)
          .then((data) => {
            setTotalUploaded((prev) => prev + 1);
            if (uploadCompleted) uploadCompleted(data);
            notifications.show({ title: "Upload Finished", message: file.name });
            mixpanel.track("Image upload finished");
            resolve(data);
          })
          .catch((err) => {
            setTotalUploaded((prev) => prev + 1);
            if (err instanceof ImageAlreadyExistsError) {
              if (uploadCompleted && err.images?.length)
                uploadCompleted(err.images);
              notifications.show({
                title: "Already in your gallery",
                message: file.name,
                color: "blue",
              });
              mixpanel.track("Image upload deduped");
              resolve(err.images ?? []);
              return;
            }
            notifications.show({
              title: "Upload Failed: " + err,
              color: "red",
              message: file.name,
            });
            mixpanel.track("Image upload failed");
            reject(err);
          });
      }),
    [uploadCompleted]
  );

  useEffect(() => {
    const processQueue = async () => {
      if (uploadQueue.length > 0 && activeUploads < MAX_PARALLEL_UPLOADS) {
        const file = uploadQueue[0];
        setUploadQueue((prev) => prev.slice(1));
        setActiveUploads((prev) => prev + 1);
        try {
          await handleUpload(file);
        } finally {
          setActiveUploads((prev) => prev - 1);
        }
      }
    };

    processQueue();
  }, [uploadQueue, activeUploads, handleUpload]);

  useEffect(() => {
    const convertToJpeg = (file) => {
      return new Promise((resolve) => {
        if (!file.type.startsWith("image/")) return resolve(file);
        const img = new window.Image();
        const reader = new FileReader();
        reader.onload = (e) => {
          img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(
              (blob) => {
                if (!blob) return resolve(file);
                const jpegFile = new File(
                  [blob],
                  file.name.replace(/\.[^.]+$/, ".jpg"),
                  { type: "image/jpeg" }
                );
                resolve(jpegFile);
              },
              "image/jpeg",
              0.85 // quality
            );
          };
          img.onerror = () => resolve(file);
          img.src = e.target.result;
        };
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
      });
    };

    const onPaste = async (event) => {
      console.debug("Paste event detected", event);
      const files = Array.from(event.clipboardData.files);
      if (files.length === 0) return;
      const processedFiles = await Promise.all(
        files.map((file) =>
          file.type === "image/png" || file.type === "image/jpeg"
            ? convertToJpeg(file)
            : file
        )
      );
      setUploadQueue((prev) => [...prev, ...processedFiles]);
    };

    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("paste", onPaste);
    };
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    setUploadQueue((prev) => [...prev, ...acceptedFiles]);
  }, []);

  return (
    <div style={{ position: "relative" }}>
      <Dropzone
        onDrop={onDrop}
        onReject={(files) => console.log("rejected files", files)}
        maxSize={4 * 1024 ** 2}
        accept={[MIME_TYPES.jpeg, MIME_TYPES.png]}
        multiple={true}
      >
        <Group
          justify="center"
          gap={textSize}
          mih={minHeight}
          style={{ pointerEvents: "none" }}
        >
          <Dropzone.Accept>
            <IconUpload
              style={{
                width: rem(iconSize),
                height: rem(iconSize),
                color: "var(--mantine-color-blue-6)",
              }}
              stroke={1.5}
            />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX
              style={{
                width: rem(iconSize),
                height: rem(iconSize),
                color: "var(--mantine-color-red-6)",
              }}
              stroke={1.5}
            />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconPhoto
              style={{
                width: rem(iconSize),
                minHeight: rem(iconSize),
                color: "var(--mantine-color-dimmed)",
              }}
              stroke={1.5}
            />
          </Dropzone.Idle>
          <div>
            <Text ta="center" size={textSize} inline>
              Tap or click to select images, or drag them here
            </Text>
            <Text ta="center" mt="sm" size="xs" c="dimmed" inline>
              Must not exceed 4 MB. Photos of people under 18 are not allowed.
            </Text>
          </div>
        </Group>
      </Dropzone>
      {(uploadQueue.length > 0 || activeUploads > 0) && (
        <Progress
          animated
          value={
            (totalUploaded +
              1 / (totalUploaded + activeUploads + uploadQueue.length + 1)) *
            100
          }
          size="sm"
          mt="sm"
          mx="xs"
        />
      )}
    </div>
  );
}

export default ImageUploader;
